import seoRoutes from './seoRoutes.json';
import { placeForPath, placeMeta } from './seoPlaces';

const SITE = 'https://flatloop.in';

/**
 * Keeps <title>, the meta description and the canonical link in step with the
 * route the visitor is actually on.
 *
 * <p>A client-rendered SPA serves one HTML file for every URL, so without this
 * every page in the site shares the home page's title — in search results, in
 * a browser's history, and in the preview card when someone pastes a link into
 * WhatsApp. Google does read titles it sees after JavaScript runs, but it reads
 * them slowly and re-crawls on its own schedule, which is why the prerender
 * step bakes the same values into the static HTML from the same table.
 *
 * <p>The table lives in seoRoutes.json rather than here precisely so
 * scripts/prerender.js can `require` it without going through the bundler. One
 * table, two readers, no way for the two to drift.
 */

const BY_PATH = seoRoutes.routes.reduce((acc, r) => {
    acc[r.path] = r;
    return acc;
}, {});

/** Longest matching prefix, so /search?q=… and /terms#section still resolve. */
export const metaForPath = (pathname) => {
    const place = placeForPath(pathname);
    if (place) return placeMeta(place);

    if (BY_PATH[pathname]) return BY_PATH[pathname];

    const trimmed = pathname.replace(/\/+$/, '');
    if (BY_PATH[trimmed]) return BY_PATH[trimmed];

    const match = seoRoutes.routes
        .filter((r) => r.path !== '/' && trimmed.startsWith(r.path))
        .sort((a, b) => b.path.length - a.path.length)[0];

    return match || BY_PATH['/'];
};

const setMeta = (selector, attr, value) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        const [, name] = selector.match(/\[(?:name|property|rel)="([^"]+)"\]/) || [];
        if (name) {
            if (selector.startsWith('link')) el.setAttribute('rel', name);
            else if (selector.includes('property=')) el.setAttribute('property', name);
            else el.setAttribute('name', name);
        }
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
};

/**
 * Per-route BreadcrumbList.
 *
 * <p>A crawler landing on /rent-insights has no way to know it sits under the
 * home page: a single-page app has no directory structure to infer one from,
 * and every route is served the same shell. Breadcrumb markup is what turns the
 * bare URL in a search result into "flatloop.in › Rent Insights", and it is one
 * of the few structured-data types answer engines use to decide how a page
 * relates to the rest of a site.
 *
 * <p>Written as a script the router replaces on every navigation, rather than
 * baked into index.html, because index.html is shared by all eight routes — one
 * static breadcrumb there would claim every page is the home page.
 */
const BREADCRUMB_ID = 'ld-breadcrumb';

const setBreadcrumb = (entry) => {
    const existing = document.getElementById(BREADCRUMB_ID);
    if (entry.path === '/') {
        // The home page is the root of the trail; a one-item breadcrumb is noise.
        if (existing) existing.remove();
        return;
    }

    const el = existing || document.createElement('script');
    el.id = BREADCRUMB_ID;
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'FlatLoop', item: SITE + '/' },
            { '@type': 'ListItem', position: 2, name: entry.crumb || entry.title, item: SITE + entry.path },
        ],
    });
    if (!existing) document.head.appendChild(el);
};

const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

/**
 * Place pages that have not cleared the inventory threshold are reachable and
 * work, but must not be indexed.
 *
 * <p>They are linked from the home page — every city tile is a real anchor so a
 * crawler can follow it — and they are deliberately not prerendered, so a
 * crawler that follows one receives the bare SPA shell: thin content that
 * duplicates the home page. `noindex, follow` is what reconciles the two: the
 * link still passes through to the pages that do deserve ranking, and the empty
 * page itself never enters the index. Held-back places are promoted by
 * re-running `npm run seo:places` once listings exist.
 */
const setRobots = (entry) => {
    const noindex = entry.place && entry.place.index === false;
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, follow' : DEFAULT_ROBOTS);
};

/**
 * Applies the entry for `pathname`. Safe to call on every route change.
 */
export const applyDocumentMeta = (pathname) => {
    const entry = metaForPath(pathname);
    if (!entry) return;

    document.title = entry.title;
    setMeta('meta[name="description"]', 'content', entry.description);
    setMeta('link[rel="canonical"]', 'href', SITE + entry.path);

    // Share cards read the OG/Twitter pair, not the plain description, so a
    // link pasted into WhatsApp shows the page you linked rather than the home
    // page. This is the half people usually forget.
    setMeta('meta[property="og:title"]', 'content', entry.title);
    setMeta('meta[property="og:description"]', 'content', entry.description);
    setMeta('meta[property="og:url"]', 'content', SITE + entry.path);
    setMeta('meta[name="twitter:title"]', 'content', entry.title);
    setMeta('meta[name="twitter:description"]', 'content', entry.description);
    setMeta('meta[name="twitter:url"]', 'content', SITE + entry.path);

    setBreadcrumb(entry);
    setRobots(entry);
};

export default applyDocumentMeta;
